import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { getConsent, saveConsent, initConsentMode, OPEN_PREFERENCES_EVENT } from "../lib/consent";

export default function CookieConsent() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const cardRef = useRef(null);
  const isHidden = !visible || pathname.startsWith("/app");

  useEffect(() => {
    initConsentMode();
    if (!getConsent()) setVisible(true);
    const openPrefs = () => {
      const existing = getConsent();
      setAnalytics(!!existing?.analytics);
      setMarketing(!!existing?.marketing);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, openPrefs);
  }, []);

  // Reserve space at the bottom of the page so the banner never covers the footer.
  useEffect(() => {
    if (isHidden) {
      document.body.style.paddingBottom = "";
      return;
    }
    const apply = () => {
      const height = cardRef.current?.offsetHeight || 0;
      document.body.style.paddingBottom = height ? `${height + 32}px` : "";
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
    };
  }, [isHidden, showPrefs]);

  const decide = (choice) => {
    saveConsent(choice);
    setShowPrefs(false);
    setVisible(false);
  };

  if (isHidden) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-5 sm:pb-5" data-testid="cookie-consent-banner">
      <div ref={cardRef} className="glossy-card pointer-events-auto mx-auto max-w-5xl rounded-2xl border border-border/80 bg-card/95 p-5 shadow-2xl backdrop-blur-xl animate-in fade-in-0 slide-in-from-bottom-4 duration-500 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 sm:flex">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-base font-semibold">We value your privacy</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              We use essential cookies to keep you signed in and secure. With your permission we'd also like to use analytics and
              marketing cookies to understand how hireqix is used. You can change your choice at any time. Read our{" "}
              <Link to="/privacy" className="font-medium text-primary hover:underline">Privacy &amp; Cookie Policy</Link>.
            </p>

            {showPrefs && (
              <div className="mt-5 space-y-3" data-testid="cookie-preferences-panel">
                <PrefRow
                  title="Strictly necessary"
                  description="Sign-in, session security and CSRF protection. These cannot be switched off."
                  checked
                  disabled
                  testId="cookie-toggle-essential"
                />
                <PrefRow
                  title="Analytics"
                  description="Anonymous usage statistics so we can improve the product."
                  checked={analytics}
                  onChange={setAnalytics}
                  testId="cookie-toggle-analytics"
                />
                <PrefRow
                  title="Marketing"
                  description="Measuring campaign performance and showing you relevant hireqix ads."
                  checked={marketing}
                  onChange={setMarketing}
                  testId="cookie-toggle-marketing"
                />
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <Button onClick={() => decide({ analytics: true, marketing: true })} data-testid="cookie-accept-all-btn">
                Accept all
              </Button>
              <Button variant="outline" onClick={() => decide({ analytics: false, marketing: false })} data-testid="cookie-reject-btn">
                Reject non-essential
              </Button>
              {showPrefs ? (
                <Button variant="ghost" onClick={() => decide({ analytics, marketing })} data-testid="cookie-save-prefs-btn">
                  Save my choices
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => setShowPrefs(true)} data-testid="cookie-manage-prefs-btn">
                  Manage preferences
                </Button>
              )}
            </div>
          </div>
          <button
            onClick={() => decide({ analytics: false, marketing: false })}
            aria-label="Reject non-essential cookies and close"
            data-testid="cookie-close-btn"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, description, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border/70 bg-muted/30 p-3.5">
      <div>
        <div className="text-sm font-medium">{title}</div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );
}
